import { BiLibrary } from "react-icons/bi";
import { FaCheckCircle } from "react-icons/fa";
import { FaDatabase } from "react-icons/fa6";
import { FiActivity, FiRefreshCw, FiUpload } from "react-icons/fi";
import { LuBrainCircuit, LuFileJson, LuFileSpreadsheet } from "react-icons/lu";

export const VisualRenderer = ({ visual, isActive }) => {
  if (!isActive) return null;

  switch (visual) {
    case "librarian":
      return (
        <div className="w-full h-full flex flex-col items-center justify-center relative overflow-hidden">
          {/* Central Brain */}
          <div className="w-24 h-24 bg-[#0B0C10] border-2 border-[#45A29E] rounded-full flex items-center justify-center z-20 relative shadow-[0_0_40px_rgba(69,162,158,0.3)]">
            <LuBrainCircuit size={40} className="text-[#45A29E]" />
            <div className="absolute inset-0 border border-[#45A29E] rounded-full animate-[pulse-ring_3s_infinite]"></div>
          </div>

          {/* Orbiting Books (Knowledge) */}
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="absolute top-1/2 left-1/2 text-[#45A29E]"
              style={{
                animation: `orbit 8s linear infinite`,
                animationDelay: `${i * -1.2}s`,
                width: "1px",
                height: "1px", // Center point
              }}
            >
              <div
                className="absolute"
                style={{ transform: "translate(-50%, -50%)" }}
              >
                {" "}
                {/* Content wrapper to keep icon upright optional, simpler here */}
                <BiLibrary size={18} className="text-[#45A29E]/80" />
              </div>
            </div>
          ))}

          <div className="absolute bottom-20 glass px-4 py-2 rounded-full text-xs text-[#45A29E] font-mono animate-bounce">
            Searching Index...
          </div>
        </div>
      );

    case "chat":
      return (
        <div className="w-full h-full flex flex-col justify-end pb-32 px-6 relative">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_#45A29E10,_transparent_70%)]"></div>

          {/* Chat Messages */}
          <div className="space-y-4">
            {/* Effie Msg */}
            <div className="flex gap-3 animate-[chat-up_0.5s_forwards]">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#45A29E] to-blue-600 flex items-center justify-center text-[10px] font-bold text-black shadow-lg">
                AI
              </div>
              <div className="glass-strong p-3 rounded-2xl rounded-tl-none text-xs text-slate-200 max-w-[85%] shadow-lg">
                Training mode active. Let's test a Logistics scenario. What
                happens if shipment #404 is delayed?
              </div>
            </div>

            {/* User Msg */}
            <div
              className="flex gap-3 justify-end animate-[chat-up_0.5s_forwards]"
              style={{
                animationDelay: "0.8s",
                opacity: 0,
                animationFillMode: "forwards",
              }}
            >
              <div className="bg-[#45A29E] p-3 rounded-2xl rounded-tr-none text-xs text-black font-medium max-w-[85%] shadow-lg">
                The system should trigger an alert to the Supply Chain Manager
                immediately.
              </div>
              <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-[10px] text-white">
                You
              </div>
            </div>

            {/* Analysis Tags popping up */}
            <div
              className="flex gap-2 flex-wrap pt-2 animate-[chat-up_0.5s_forwards]"
              style={{
                animationDelay: "1.5s",
                opacity: 0,
                animationFillMode: "forwards",
              }}
            >
              <span className="px-2 py-1 bg-purple-500/20 border border-purple-500/50 text-purple-300 text-[9px] rounded-md">
                Logistics
              </span>
              <span className="px-2 py-1 bg-blue-500/20 border border-blue-500/50 text-blue-300 text-[9px] rounded-md">
                Urgency: High
              </span>
              <span className="px-2 py-1 bg-green-500/20 border border-green-500/50 text-green-300 text-[9px] rounded-md">
                Captured
              </span>
            </div>
          </div>
        </div>
      );

    case "upload":
      return (
        <div className="w-full h-full flex flex-col items-center justify-center relative">
          {/* Central Database Hub */}
          <div className="w-32 h-32 bg-[#0B0C10] border-2 border-dashed border-[#45A29E]/50 rounded-2xl flex items-center justify-center mb-16 relative shadow-[0_0_30px_rgba(69,162,158,0.2)]">
            <FaDatabase size={40} className="text-[#45A29E]" />
            {/* Inner Glow */}
            <div className="absolute inset-0 bg-[#45A29E]/10 animate-pulse rounded-2xl"></div>
          </div>

          {/* Files Falling In */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-1/3 left-1/4 flex flex-col items-center animate-[ingest_3s_infinite]">
              <LuFileSpreadsheet className="text-green-400" size={24} />
              <span className="text-[8px] text-green-400 mt-1">FAQ.xlsx</span>
            </div>
            <div
              className="absolute top-1/3 right-1/4 flex flex-col items-center animate-[ingest_4s_infinite]"
              style={{ animationDelay: "1.5s" }}
            >
              <LuFileJson className="text-amber-400" size={24} />
              <span className="text-[8px] text-amber-400 mt-1">Data.json</span>
            </div>
          </div>

          <div className="glass px-6 py-3 rounded-full flex items-center gap-2 text-[#45A29E] text-xs font-bold border-[#45A29E]/30">
            <FiUpload size={14} /> Injecting Knowledge
          </div>
        </div>
      );

    case "scanner":
      return (
        <div className="w-full h-full flex flex-col items-center justify-center relative">
          {/* Document Card */}
          <div className="w-64 h-80 bg-slate-800 border border-slate-700 rounded-xl relative overflow-hidden shadow-2xl flex flex-col p-4">
            {/* Text lines */}
            <div className="space-y-4 opacity-30">
              <div className="h-2 bg-white rounded w-3/4"></div>
              <div className="h-2 bg-white rounded w-full"></div>
              <div className="h-2 bg-white rounded w-5/6"></div>
              <div className="h-2 bg-white rounded w-full"></div>
              <div className="h-2 bg-white rounded w-2/3"></div>
            </div>

            {/* Highlighted Error (Red) -> Fixed (Green) */}
            <div className="absolute top-1/3 left-4 right-4 h-8 border border-red-500/50 bg-red-500/10 rounded flex items-center px-2">
              <div className="text-[8px] text-red-400 uppercase font-bold flex items-center gap-1">
                <span className="animate-ping w-1 h-1 bg-red-500 rounded-full"></span>{" "}
                Accuracy Low
              </div>
            </div>

            {/* Scanning Bar */}
            <div className="absolute left-0 w-full h-[2px] bg-[#45A29E] shadow-[0_0_15px_#45A29E] animate-[scan-bar_3s_linear_infinite]"></div>
          </div>

          <div className="mt-8 flex gap-4">
            <div className="glass px-3 py-2 rounded text-[10px] text-slate-400 flex flex-col items-center">
              <span className="text-[#45A29E] text-lg font-bold">98%</span>
              Accuracy
            </div>
            <div className="glass px-3 py-2 rounded text-[10px] text-slate-400 flex flex-col items-center">
              <span className="text-[#45A29E] text-lg font-bold">0.0s</span>
              Latency
            </div>
          </div>
        </div>
      );

    case "evolution":
      return (
        <div className="w-full h-full flex flex-col items-center justify-center relative">
          {/* Orbital Rings */}
          <div className="absolute w-64 h-64 border border-[#45A29E]/20 rounded-full animate-[spin-slow_10s_linear_infinite]"></div>
          <div className="absolute w-48 h-48 border border-purple-500/20 rounded-full animate-[spin-slow_7s_linear_infinite_reverse]"></div>

          {/* Central Core */}
          <div className="relative w-24 h-24 bg-[#0B0C10] border border-white/10 rounded-full flex items-center justify-center z-10 shadow-[0_0_50px_rgba(255,255,255,0.05)]">
            <FiRefreshCw
              size={32}
              className="text-white animate-spin"
              style={{ animationDuration: "4s" }}
            />
          </div>

          {/* Floating Metrics */}
          <div className="absolute top-1/4 right-8 glass-strong px-2 py-1 rounded text-[9px] text-[#45A29E] animate-[float_3s_infinite]">
            <FiActivity size={10} className="inline mr-1" /> Optimization
          </div>
          <div
            className="absolute bottom-1/4 left-8 glass-strong px-2 py-1 rounded text-[9px] text-purple-400 animate-[float_4s_infinite]"
            style={{ animationDelay: "1s" }}
          >
            <FaCheckCircle size={10} className="inline mr-1" /> Feedback Loop
          </div>

          <div className="absolute bottom-12 w-full text-center">
            <p className="text-xs text-slate-500 font-mono">
              Continuous Evolution Cycle
            </p>
          </div>
        </div>
      );

    default:
      return null;
  }
};
